import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Fade,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  RemoveCircle as StableIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

type TrafficLightStatus = 'green' | 'amber' | 'red';

interface KpiDataPoint {
  date: string;
  value: number;
  target: number;
}

interface AuditLink {
  audit_id: number;
  audit_name: string;
  audit_date: string;
  is_completed: boolean;
}

interface MockKpi {
  id: string;
  name: string;
  category: string;
  unit: string;
  dataPoints: KpiDataPoint[];
  auditLinks?: AuditLink[];
  hasAuditLinks?: boolean;
}

interface LandingPageKpiDashboardProps {
  kpis: MockKpi[];
  showAuditMarkers?: boolean;
  onViewAuditDetails?: (kpiId: string, kpiName: string, period?: string) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Inverse metrics where LOWER values are better
 * (e.g., defect rate, expense ratio, turnover, response time, cycle time)
 */
const INVERSE_METRICS = [
  'defect-rate',
  'operating-expense-ratio',
  'cash-conversion-cycle',
  'staff-turnover',
  'response-time',
];

/**
 * Check if a KPI is an inverse metric (lower is better)
 */
const isInverseMetric = (kpiId: string): boolean => {
  return INVERSE_METRICS.includes(kpiId);
};

/**
 * Calculate traffic light status based on value vs target
 * Handles both standard (higher is better) and inverse (lower is better) metrics
 * 
 * Performance percentage always represents "how good is this?":
 * - 100% = on target
 * - >100% = better than target
 * - <100% = worse than target
 * 
 * Green: On target or better (≥100%)
 * Amber: Within tolerance (80-99%)
 * Red: Below tolerance (<80%)
 */
const getTrafficLightStatus = (value: number, target: number, kpiId: string): TrafficLightStatus => {
  const isInverse = isInverseMetric(kpiId);
  
  // Calculate performance percentage (always represents "goodness")
  let performancePercentage: number;
  
  if (isInverse) {
    // Lower is better: invert the calculation so better performance = higher percentage
    // If value < target (good): percentage > 100%
    // If value = target (on target): percentage = 100%
    // If value > target (bad): percentage < 100%
    performancePercentage = (target / value) * 100;
  } else {
    // Higher is better: standard calculation
    // If value > target (good): percentage > 100%
    // If value = target (on target): percentage = 100%
    // If value < target (bad): percentage < 100%
    performancePercentage = (value / target) * 100;
  }
  
  // Apply universal thresholds
  if (performancePercentage >= 100) return 'green';  // On target or better
  if (performancePercentage >= 80) return 'amber';   // Within tolerance
  return 'red';  // Below tolerance
};

/**
 * Get color for traffic light status
 */
const getStatusColor = (status: TrafficLightStatus): string => {
  switch (status) {
    case 'green': return '#4caf50';
    case 'amber': return '#ff9800';
    case 'red': return '#f44336';
  }
};

/**
 * Format value with appropriate unit
 */
const formatValue = (value: number, unit: string): string => {
  if (unit === '%') return `${value.toFixed(1)}%`;
  if (unit === '$') return `$${value.toLocaleString()}`;
  if (unit === 'days') return `${value.toFixed(0)} days`;
  if (unit === 'hours') return `${value.toFixed(1)} hrs`;
  return value.toFixed(1);
};

/**
 * Calculate trend direction
 * Handles both standard (higher is better) and inverse (lower is better) metrics
 */
const getTrendDirection = (current: number, previous: number, kpiId: string): 'up' | 'down' | 'stable' => {
  const diff = current - previous;
  const percentChange = Math.abs((diff / previous) * 100);
  
  // Consider changes < 2% as stable
  if (percentChange < 2) return 'stable';
  
  const isInverse = isInverseMetric(kpiId);
  
  // For inverse metrics: decreasing value = improvement (up trend)
  // For standard metrics: increasing value = improvement (up trend)
  let isImprovement: boolean;
  
  if (isInverse) {
    // Lower is better: negative diff = improvement
    isImprovement = diff < 0;
  } else {
    // Higher is better: positive diff = improvement
    isImprovement = diff > 0;
  }
  
  return isImprovement ? 'up' : 'down';
};

// ============================================================================
// TRAFFIC LIGHT CELL COMPONENT
// ============================================================================

interface TrafficLightCellProps {
  kpi: MockKpi;
  dataPoint: KpiDataPoint;
  previousValue?: number;
  animationDelay: number;
  showAuditMarkers?: boolean;
  onViewAuditDetails?: (kpiId: string, kpiName: string, period?: string) => void;
}

const TrafficLightCell: React.FC<TrafficLightCellProps> = ({ 
  kpi, 
  dataPoint, 
  previousValue,
  animationDelay,
  showAuditMarkers = false,
  onViewAuditDetails
}) => {
  const [visible, setVisible] = useState(false);
  const theme = useTheme();
  
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), animationDelay);
    return () => clearTimeout(timer);
  }, [animationDelay]);

  const status = getTrafficLightStatus(dataPoint.value, dataPoint.target, kpi.id);
  const color = getStatusColor(status);
  const trend = previousValue !== undefined 
    ? getTrendDirection(dataPoint.value, previousValue, kpi.id)
    : 'stable';

  const trendIcon = trend === 'up' 
    ? <TrendingUpIcon sx={{ fontSize: 14 }} />
    : trend === 'down'
    ? <TrendingDownIcon sx={{ fontSize: 14 }} />
    : <StableIcon sx={{ fontSize: 14 }} />;

  const trendColor = trend === 'up' 
    ? '#4caf50'
    : trend === 'down'
    ? '#f44336'
    : 'rgba(255,255,255,0.4)';

  // Check if any audit occurred in this period
  const periodDate = new Date(dataPoint.date);
  const auditsInPeriod = showAuditMarkers && kpi.auditLinks ? kpi.auditLinks.filter((audit) => {
    const auditDate = new Date(audit.audit_date);
    return auditDate.getFullYear() === periodDate.getFullYear() &&
           auditDate.getMonth() === periodDate.getMonth();
  }) : [];
  
  const hasAuditInPeriod = auditsInPeriod.length > 0;

  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5, minWidth: '180px' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {kpi.name}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Value: {formatValue(dataPoint.value, kpi.unit)}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Target: {formatValue(dataPoint.target, kpi.unit)}
          </Typography>
          {previousValue !== undefined && (
            <Typography variant="caption" sx={{ display: 'block', color: trendColor }}>
              {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} vs Previous Month
            </Typography>
          )}
          {hasAuditInPeriod && (
            <Typography variant="caption" sx={{ display: 'block', color: '#2196f3', mt: 0.5, fontWeight: 600 }}>
              📋 {auditsInPeriod.length} audit(s) this month
            </Typography>
          )}
        </Box>
      }
      placement="top"
      arrow
    >
      <Fade in={visible} timeout={600}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${color}15`,
            border: `2px solid ${color}`,
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${color}20 0%, transparent 100%)`,
              opacity: 0,
              transition: 'opacity 0.3s ease',
            },
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: `0 8px 20px ${color}40`,
              backgroundColor: `${color}25`,
              zIndex: 10,
              '&:before': {
                opacity: 1,
              },
            },
          }}
        >
          {/* Animated pulse effect */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle, ${color}30 0%, transparent 70%)`,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.6 },
              },
            }}
          />

          {/* Audit marker icon - top right corner */}
          {hasAuditInPeriod && (
            <Tooltip title={`${auditsInPeriod.length} audit(s) completed this month - Click to view details`} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onViewAuditDetails) {
                    onViewAuditDetails(kpi.id, kpi.name, dataPoint.date);
                  }
                }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  p: 0.25,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { 
                    bgcolor: 'primary.dark',
                    transform: 'scale(1.15)',
                  },
                  width: 20,
                  height: 20,
                  zIndex: 20,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  transition: 'all 0.2s ease',
                }}
              >
                <AssignmentIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Content */}
          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: color,
                fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                letterSpacing: '0.5px',
                textShadow: `0 0 10px ${color}40`,
              }}
            >
              {formatValue(dataPoint.value, kpi.unit)}
            </Typography>
            
            {/* Trend indicator */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                mt: 0.5,
                opacity: trend === 'stable' ? 0.5 : 1,
              }}
            >
              <Box sx={{ color: trendColor, display: 'flex', alignItems: 'center' }}>
                {trendIcon}
              </Box>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: trendColor,
                  fontWeight: 600,
                }}
              >
                {previousValue !== undefined
                  ? `${Math.abs(((dataPoint.value - previousValue) / previousValue) * 100).toFixed(0)}%`
                  : '—'
                }
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Tooltip>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const LandingPageKpiDashboard: React.FC<LandingPageKpiDashboardProps> = ({ 
  kpis, 
  showAuditMarkers = false,
  onViewAuditDetails 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get unique categories
  const categories = Array.from(new Set(kpis.map(kpi => kpi.category))).sort();
  
  // Default to first category
  const [selectedCategory, setSelectedCategory] = useState<string>(categories[0] || '');

  // Get unique dates (months) sorted chronologically
  const allDatesRaw = Array.from(
    new Set(kpis.flatMap(kpi => kpi.dataPoints.map(dp => dp.date)))
  ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  
  // On mobile, show only last 3 months; on desktop, show all
  const allDates = isMobile ? allDatesRaw.slice(-3) : allDatesRaw;

  // Filter KPIs by selected category
  const filteredKpis = kpis.filter(kpi => kpi.category === selectedCategory);

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0, 
        backgroundColor: 'transparent',
        color: '#FFFFFF',
      }}
    >
      {/* Category Filter */}
      <Box sx={{ mt: 4, mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* Category Filters */}
        {categories.map((category) => (
          <Chip
            key={category}
            label={category}
            onClick={() => setSelectedCategory(category)}
            sx={{
              backgroundColor: selectedCategory === category ? '#E53935' : 'rgba(255,255,255,0.1)',
              color: selectedCategory === category ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
              fontWeight: 600,
              border: '1px solid',
              borderColor: selectedCategory === category ? '#E53935' : 'rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: selectedCategory === category ? '#D32F2F' : 'rgba(255,255,255,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>

      {/* KPI Grid - Scrollable Container */}
      <Box
        sx={{
          maxHeight: '80vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: '12px',
            height: '0px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '10px',
            margin: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, #E53935 0%, #C62828 100%)',
            borderRadius: '10px',
            border: '2px solid rgba(0,0,0,0.3)',
            '&:hover': {
              background: 'linear-gradient(180deg, #EF5350 0%, #E53935 100%)',
            },
          },
          '&::-webkit-scrollbar-corner': {
            background: 'rgba(255,255,255,0.03)',
          },
          // Firefox scrollbar styling
          scrollbarWidth: 'thin',
          scrollbarColor: '#E53935 rgba(255,255,255,0.05)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `200px repeat(${allDates.length}, 1fr)`,
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
        {/* Header Row */}
        <Box
          sx={{
            p: 1.5,
            textAlign: 'left',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(229, 57, 53, 0.15)' 
              : 'rgba(229, 57, 53, 0.12)',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(229, 57, 53, 0.3)'
              : 'rgba(229, 57, 53, 0.5)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.9)'
                : '#E53935',
            }}
          >
            KPI (TARGET)
          </Typography>
        </Box>
        {allDates.map((date, index) => (
          <Box
            key={date}
            sx={{
              p: 1.5,
              textAlign: 'center',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(229, 57, 53, 0.15)'
                : 'rgba(229, 57, 53, 0.12)',
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(229, 57, 53, 0.3)'
                : 'rgba(229, 57, 53, 0.5)',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `slideInFromTop 0.6s ease-out ${index * 0.05}s both`,
              '@keyframes slideInFromTop': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(-20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.9)'
                  : '#E53935',
              }}
            >
              {new Date(date).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })}
            </Typography>
          </Box>
        ))}

        {/* KPI Rows */}
        {filteredKpis.map((kpi, kpiIndex) => (
          <React.Fragment key={kpi.id}>
            {/* KPI Name Cell */}
            <Box
              sx={{
                p: 1.5,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.03)',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgba(0,0,0,0.12)',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                animation: `slideInFromLeft 0.6s ease-out ${kpiIndex * 0.05}s both`,
                '@keyframes slideInFromLeft': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateX(-20px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateX(0)',
                  },
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.05)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                {showAuditMarkers && kpi.hasAuditLinks && (
                  <Tooltip title={`Click to view all ${kpi.auditLinks?.length || 0} linked audit(s)`} arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (onViewAuditDetails) {
                          onViewAuditDetails(kpi.id, kpi.name);
                        }
                      }}
                      sx={{ 
                        p: 0.25, 
                        mt: 0.25,
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' }
                      }}
                    >
                      <AssignmentIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#212121',
                      lineHeight: 1.4,
                    }}
                  >
                    {kpi.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.5)'
                        : 'rgba(0,0,0,0.5)',
                      fontSize: '0.75rem',
                      mt: 0.5,
                    }}
                  >
                    Target: {formatValue(kpi.dataPoints[0]?.target || 0, kpi.unit)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Data Cells */}
            {allDates.map((date, dateIndex) => {
              const dataPoint = kpi.dataPoints.find(dp => dp.date === date);
              // For mobile with only 3 months, find the actual previous value from full dataset
              const allKpiDates = kpi.dataPoints.map(dp => dp.date).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
              const currentDateIndex = allKpiDates.indexOf(date);
              const previousDataPoint = currentDateIndex > 0 
                ? kpi.dataPoints.find(dp => dp.date === allKpiDates[currentDateIndex - 1])
                : undefined;

              return (
                <Box 
                  key={`${kpi.id}-${date}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {dataPoint ? (
                    <TrafficLightCell
                      kpi={kpi}
                      dataPoint={dataPoint}
                      previousValue={previousDataPoint?.value}
                      animationDelay={kpiIndex * 50 + dateIndex * 30}
                      showAuditMarkers={showAuditMarkers}
                      onViewAuditDetails={onViewAuditDetails}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        border: '1px dashed rgba(255,255,255,0.1)',
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          color: 'rgba(255,255,255,0.3)',
                        }}
                      >
                        —
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        {[
          { status: 'green' as TrafficLightStatus, label: 'On or better than target' },
          { status: 'amber' as TrafficLightStatus, label: 'Within tolerance' },
          { status: 'red' as TrafficLightStatus, label: 'Below tolerance' },
        ].map(({ status, label }) => (
          <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: getStatusColor(status),
                boxShadow: `0 0 10px ${getStatusColor(status)}40`,
              }}
            />
            <Typography
              sx={{
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default LandingPageKpiDashboard;
